import Box from '@mui/material/Box';

// Stroke icons transcribed from the reference's Navbar5 — same viewBox,
// same path data, same stroke weights and default sizes.
//
// The chrome previously used @mui/icons-material, which is a different
// drawing system: Material's glyphs are heavier, built from filled shapes
// rather than strokes, and in two places they are not even the same object
// — the reference's basket is a shopping *bag*, not a trolley, and its
// burger has a short third rule. Against the reference's thin-stroke set
// the navbar read as a different product, so these are drawn here instead.
//
// Every icon takes `size` (px, square) and inherits `currentColor`, so a
// parent's colour and hover transition apply with no extra wiring.
const Glyph = ({ size, stroke, children, sx, ...rest }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    sx={{ width: size, height: size, flex: 'none', display: 'block', ...sx }}
    {...rest}
  >
    {children}
  </Box>
);

// City chip — map pin, then the re-detect arrow beside the label.
export const PinIcon = ({ size = 16, stroke = 2, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </Glyph>
);

export const RefreshIcon = ({ size = 14, stroke = 2.2, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M20 11a8 8 0 1 0-2.3 5.7" />
    <path d="M20 4v7h-7" />
  </Glyph>
);

// Document. The reference adds a tick inside it on the "My Reports" pill
// and leaves it off in the account menu.
export const ReportIcon = ({ size = 18, stroke = 1.8, check = false, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
    {check && <path d="m9 15 2 2 4-4" />}
  </Glyph>
);

export const BellIcon = ({ size = 21, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </Glyph>
);

// A shopping bag, which is what the reference draws — not a trolley.
export const BagIcon = ({ size = 21, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <path d="M3 6h18" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </Glyph>
);

export const UserIcon = ({ size = 20, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </Glyph>
);

// Speech bubble — the reference's "My Tickets".
export const TicketIcon = ({ size = 18, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </Glyph>
);

export const LogoutIcon = ({ size = 18, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </Glyph>
);

// The reference has no sign-in glyph — its guest menu is two buttons — so
// this mirrors the logout door rather than borrowing from another set.
export const LoginIcon = ({ size = 18, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="M10 17l-5-5 5-5M3 12h12" />
  </Glyph>
);

export const UserPlusIcon = ({ size = 18, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <circle cx="10" cy="8" r="4" />
    <path d="M3 21a7 7 0 0 1 12.2-4.7" />
    <path d="M18 14v6M15 17h6" />
  </Glyph>
);

// The third rule is deliberately short — that is how the reference draws it.
export const BurgerIcon = ({ size = 22, stroke = 2, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M4 7h16M4 12h16M4 17h10" />
  </Glyph>
);

export const CloseIcon = ({ size = 22, stroke = 2, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Glyph>
);

export const TrashIcon = ({ size = 16, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </Glyph>
);

export const CheckIcon = ({ size = 22, stroke = 2, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Glyph>
);

export const DoubleCheckIcon = ({ size = 14, stroke = 2, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M1.5 13.5 5 17l6.5-8" />
    <path d="M10 14.5 12.5 17 22 6" />
  </Glyph>
);

export const ArrowRightIcon = ({ size = 18, stroke = 2, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Glyph>
);

// The floating WhatsApp action. The reference draws the bubble as a
// stroke and the handset inside it as a fill, which is why this one
// carries its own fill/stroke overrides.
// "Pick on map" on the booking address.
export const MapIcon = ({ size = 18, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M9 3 3 5.5v15L9 18l6 3 6-2.5v-15L15 6 9 3z" />
    <path d="M9 3v15M15 6v15" />
  </Glyph>
);

// Role-select modal: the centre, and the doctor working at it.
export const BuildingIcon = ({ size = 22, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M3 21h18" />
    <path d="M5 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16" />
    <path d="M16 21V9h3a2 2 0 0 1 2 2v10" />
    <path d="M9 7h3M9 11h3M9 15h3" />
  </Glyph>
);

export const StethoscopeIcon = ({ size = 22, stroke = 1.8, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M6 3v5a4 4 0 0 0 8 0V3" />
    <path d="M6 3H4.5M14 3h1.5" />
    <path d="M10 12v2a5 5 0 0 0 5 5 4 4 0 0 0 4-4v-1" />
    <circle cx="19" cy="10" r="2" />
  </Glyph>
);

export const WhatsAppIcon = ({ size = 26, stroke = 1.9, ...p }) => (
  <Glyph size={size} stroke={stroke} {...p}>
    <path d="M3.5 20.5l1.4-4.2A8.5 8.5 0 1 1 8 19.3z" />
    <path
      d="M9 8.6c.2 2.6 3.4 5.8 6 6l1-1.4-2-1-1 .8c-1-.4-2.1-1.5-2.5-2.5l.8-1-1-2z"
      fill="currentColor"
      stroke="none"
    />
  </Glyph>
);
